import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Snackbar,
  Tab,
  IconButton,
  Tabs,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Paper,
  Chip,
  // --- Start: Added Imports for Alert Form ---
  MenuItem,
  FormLabel,
  Collapse,
  Autocomplete,
  CircularProgress,
  Checkbox,
  // --- Added Dialog imports for delete confirmation ---
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Divider, // Added Divider
  Accordion, // Added for new UI
  AccordionSummary, // Added for new UI
  AccordionDetails,
  Select,
  TextField, // Added for new UI
  // --- End: Added Imports for Alert Form ---
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

// --- End: Added Imports ---
import {
  getConfigDetails,
  getAlertKpiProgramsSectors,
  saveAlertConfig,
  updateAlertConfig,
  createDataPipelineProgram,
  editIbpGdo,
  getProgramSectorList,
  getProgramById,
  getProgramList,
  addOrUpdateAlertSlaProject,
  createAlertConfig,
  getAlertConfigsByProgram,
  updateAlertConfigs,
  deleteAlertConfigById,
  deleteAlertSlaProject,
} from 'service/service';
import Loader from 'components/Loader/loader';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import UploadIcon from '../../assets/hugeicons_file-upload1.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SuccessConfirmationDialog from 'components/AlertKpi/SuccessModal';
import ErrorDialog from 'components/AlertKpi/ErrorModal';
import { ProgramTab } from './ProgramTab';
import { ProjectTab } from './ProjectTab';

import moment from 'moment';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import colors from 'assets/theme/base/colors';
import {
  buildElasticsearchSourceFields,
  convertDotNotationToNested,
} from 'utils/alertPayloadTransformers';
import PageWrapper from 'components/Common/PageWraper';
import { useTheme } from '@mui/material/styles'; // Added useTheme

// --- Added AlertErrorDialog import from reference ---
// import AlertErrorDialog from './AlertErrorDialog';
import { JobsPipelinesTab } from './JobsPipelinesTab';
import SLATab from './SLATab';
import { AlertsTab as IbpAlertsTab } from './AlertsTab';
import GBulkUploadModal from './GBulkUploadModal';

export interface ProgramVariableRow {
  isEnabled: boolean;
  type: string;
  name: string;
  value: string;
  identifier: string;
  uuid: string;
}

interface projectData {
  id?: number | null;
  projects: string;
  alertType: string;
  channel: string;
  recipients: string[];
  more?: number;
  isEditing: boolean;
  Alert_ID?: string;
  HopName?: string;
  templateName?: string;
  payload?: any;
  serviceNowUrl?: string;
  emailBody?: string;
  emailSubject?: string; // Added for edit
  title?: string;
  body?: string;
  teamsMail?: string;
  allRecipients?: string; // Added for edit
}

// ===================================================================
// START: TYPESCRIPT FIX
// Define a strong type for the alert form data
// ===================================================================
type AlertType =
  | 'SLA Missed'
  | 'SLA Met'
  | 'SLA Missed, SLA Met'
  | 'Pipeline Failures'
  | 'Data Asset Failures';

interface AlertData {
  hopName: string;
  alertType: string;
  channel: string;
  templateName: string;
  emailBody: string;
  recipients: string;
  deliveryChannel: string;
  projects: string[];
  emailSubject: string;
  payload: {
    description: string;
    ConfigurationItem: string;
    resource: string;
    metric_name: string;
    AssignmentGroup: string;
    severity: string;
    source: string;
    type: string;
  };
  serviceNowUrl: string;
  title: string;
  body: string;
  teamsMail: string;
}
// ===================================================================
// END: TYPESCRIPT FIX
// ===================================================================

// Icons for multi-select checkboxes - custom white icons for blue background
const icon = (
  <Box
    sx={{
      width: 20,
      height: 20,
      border: '2px solid white',
      borderRadius: '2px',
      backgroundColor: 'transparent',
    }}
  />
);
const checkedIcon = (
  <Box
    sx={{
      width: 20,
      height: 20,
      border: '2px solid white',
      borderRadius: '2px',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '6px',
        top: '2px',
        width: '5px',
        height: '10px',
        border: 'solid rgb(0, 55, 113)', // Checkmark matches background
        borderWidth: '0 2px 2px 0',
        transform: 'rotate(45deg)',
      },
    }}
  />
);

interface ProgramTabDashboardIbpProps {
  breadcrumbPrefixLabel?: string;
  breadcrumbLink?: string;
  showUploadAction?: boolean;
  onUploadClick?: () => void;
}

const ProgramTabDashboardIbp = ({
  breadcrumbPrefixLabel = 'Markets',
  breadcrumbLink = '/data-pipeline/programOps/IBP/markets/ibp-dashboard',
  showUploadAction = false,
  onUploadClick,
}: ProgramTabDashboardIbpProps) => {
  const theme = useTheme(); // Added theme
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');
  const initialTab = tabParam ? parseInt(tabParam) : 0;
  const [activeTab, setActiveTab] = useState(initialTab);

  const [openSnackbar, setOpenSnackBar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<any>({});
  const { id: configId } = useParams<{ id: string }>();
  const [alertId, setAlertId] = useState(configId || null);
  const [variables, setVariables] = useState<ProgramVariableRow[]>([]);
  const [maintenanceWindows, setMaintenanceWindows] = useState<any>([]);
  const [groupingAlertsConfig, setGroupingAlertsConfig] = useState({});
  const [openDialogSuccess, setOpenDialogSuccess] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [programError, setProgramError] = useState('');
  const [programOptions, setProgramOptions] = useState([{ title: '', value: '' }]);
  const [sectorOptions, setSectorOptions] = useState([{ title: '', value: '' }]);
  const [snackBar, setSnackbarLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean>(true);

  const [formData, setFormData] = useState<any>({});

  const [startDate, setStartDate] = useState(
    moment().tz('America/Chicago').startOf('day').format('YYYY-MM-DD'),
  );
  const [endDate, setEndDate] = useState(
    moment().tz('America/Chicago').endOf('day').format('YYYY-MM-DD'),
  );
  const [projectList, setProjectList] = useState<any>([]);
  const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });
  const [programDetail, setProgramDetail] = useState<any>({});
  const { prId, prName } = useParams();
  const [searchProject, setSearchProject] = useState('');
  const [toast, setToast] = useState<any>({
    open: false,
    message: '',
  });
  // const [searchParams] = useSearchParams();
  // const programID = searchParams.get('programID');
  // const programName = searchParams.get('programName');
  const [programCreated, setProgramCreated] = useState(false);
  const [isJobsTabActive, setIsJobsTabActive] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');

    // Enable the flag if tab === '2'
    setIsJobsTabActive(tab === '2');
    setActiveTab(tab ? Number.parseInt(tab, 10) : 0);
  }, [location.search]);

  const { pathname, searchParams } = new URL(window.location.href);
  const params = new URLSearchParams(searchParams);
  let programID = params.get('programID');
  let programName = params.get('programName');
  const isEdited = queryParams.get('isEdited') === 'true';

  useEffect(() => {
    if (programName && programID) {
      setFormData((prevData: any) => ({
        ...prevData,
        program: programName,
        id: programID,
      }));
    }
  }, [programName, programID]);

  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [anchorElDetails, setAnchorElDetails] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorElDetails);
  const [sectorMasterList, setSectorMasterList] = useState<any[]>([]);
  const [sectorList, setSectorList] = useState<any[]>([]);
  const [sectorName, setSectorName] = useState('all');
  const hiddenSector = 'SODASTREAM';
  const [programList, setProgramList] = useState<any>([]);
  const [projectData, setProjectData] = useState<any[]>([]);
  // const updatedSectorOptionsList = sectorList
  // .filter(sector => sector !== hiddenSector)
  // .map(sector => {
  //  if (sector === "Europe") {
  //  return sector.toUpperCase();
  //  }
  //  return sector;
  // });
  const filteredSectorList = sectorList.filter((sector) => sector !== hiddenSector);
  const { id, name } = useParams();
  const navigate = useNavigate();

  const getProgramData = async (sectorId?: string, searchKeyword?: string) => {
    setLoading(true);
    let data: any = await getProgramList(sectorId, searchKeyword);
    setLoading(false);
    setProgramList(data.data.programData);
  };

  useEffect(() => {
    const activeId = document.getElementById('root');
    if (window.location.pathname.includes('/data-pipeline/programOps/IBP/markets/ibp-dashboard')) {
      if (activeId) {
        document.head.classList.add('theme-bg-blue');
        document.body.classList.add('theme-bg-blue');
      }
    }
    if (localStorage.getItem('filterSectors') && localStorage.getItem('sectorsMasterData')) {
      setSectorList(JSON.parse(localStorage.getItem('filterSectors') ?? ''));
      setSectorMasterList(JSON.parse(localStorage.getItem('sectorsMasterData') ?? ''));
    } else {
      getSectorData();
    }
  }, []);

  useEffect(() => {
    if (openModal && !openModal.isOpen && openModal.closeType && openModal.closeType == 'updated') {
      let sectorId;
      if (sectorName && sectorName != 'all') {
        sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id;
      }
      setSearchProject('');
      getProgramDetails(sectorId);
    }
    if (programName) {
      setFormData((prevFormData: any) => ({
        ...prevFormData,
        program: programName,
      }));
    }
  }, [openModal, programName]);

  const getSectorData = async () => {
    let sectors: any = await getProgramSectorList();
    const filterSectors = sectors.data.sectors.map((sec: any) => sec.Sector_Name);
    localStorage.setItem('filterSectors', JSON.stringify(filterSectors));
    localStorage.setItem('sectorsMasterData', JSON.stringify(sectors.data.sectors));
    setSectorList(filterSectors);
    setSectorMasterList(sectors.data.sectors);
  };

  const getProgramDetails = async (sectorId?: string, date?: string) => {
    setLoading(true);
    let result = await getProgramById(programID, sectorId, date || startDate);
    setLoading(false);
    setProgramDetail(result.data?.programData ? result.data.programData[0] : '');
  };

  const handleEditClose = (closeType: any) => {
    setOpenModal({
      ...openModal,
      isOpen: false,
      closeType: closeType,
    });
    setSelectedRows([]);
  };

  const getToastData = (data: any) => {
    setToast({
      ...toast,
      message: data.data.message,
    });
  };
  const handleTabChange = async (_: React.SyntheticEvent, newValue: number) => {
    // ===================================================================
    // START: STATE PRESERVATION FIX
    // We no longer need to save/update on tab change, just set the active tab.
    // The state is preserved in the parent component.
    // ===================================================================
    // setLoading(true); // NO LONGER NEEDED
    try {
      // if ((newValue > activeTab)) { // NO LONGER NEEDED
      //      if (!isEdited) {
      //          await handleSaveNext();
      //      } else {
      //          await handleUpdateSave();
      //      }
      // }

      const nextQueryParams = new URLSearchParams(location.search);
      nextQueryParams.set('tab', String(newValue));
      setActiveTab(newValue);
      navigate(`${location.pathname}?${nextQueryParams.toString()}`, { replace: true });
    } finally {
      // setLoading(false); // NO LONGER NEEDED
    }
    // ===================================================================
    // END: STATE PRESERVATION FIX
    // ===================================================================
  };

  const handleClose = () => {
    setOpenSnackBar(false);
  };

  const getApiErrorMessage = (result: any, fallbackMessage = 'Unable to create GDO.') => {
    if (!result) {
      return fallbackMessage;
    }
    if (result?.success === false) {
      return result?.data?.message || result?.message || fallbackMessage;
    }
    if (result?.statusCode >= 400) {
      return result?.data?.message || result?.message || fallbackMessage;
    }
    if (result?.response?.status >= 400) {
      return (
        result?.response?.data?.data?.message ||
        result?.response?.data?.message ||
        result?.response?.data ||
        result?.message ||
        fallbackMessage
      );
    }
    return '';
  };

  const handleSaveNext = async () => {
    if (programCreated && activeTab === 4) {
      navigate(`/data-pipeline/programOps/IBP/markets/ibp-dashboard`);
    }
    if (programCreated) {
      setActiveTab(activeTab + 1);
      return;
    }
    if (activeTab == 0) {
      if (!isFormValid()) {
        alert('Please fill in all mandatory fields.');
        return;
      } else {
        setProgramError('');
        setSnackbarLoading(true);

        const formPayload = {
          programName: formData?.program,
          businessProgram: 'IBP',
        };

        programName = formData?.program;
        const result = await createDataPipelineProgram(formPayload);
        const apiError = getApiErrorMessage(result);

        if (apiError) {
          setProgramError(apiError);
          setSnackbarLoading(false);
          return;
        }

        const programId = result?.data?.programId || result?.programId;
        const programNameResponse = result?.data?.programName || result?.programName || formData?.program;

        if (!programId) {
          setProgramError(result?.data?.message || 'Unable to create GDO.');
          setSnackbarLoading(false);
          return;
        }

        const nextTab = activeTab + 1;
        setActiveTab(nextTab);
        setProgramCreated(true);
        setOpenSnackBar(true);
        setSnackbarLoading(false);

        if (location.pathname.includes('/ibpDashboard/gdoTabs')) {
          navigate(
            `${location.pathname}?tab=${nextTab}&programID=${programId}&programName=${encodeURIComponent(
              programNameResponse,
            )}`,
          );
        } else {
          navigate(
            `/data-pipeline/ibp-dashboard/programs-tab?programID=${programId}&programName=${encodeURIComponent(
              programNameResponse,
            )}`,
          );
        }
      }
    } else if (activeTab >= 1 && activeTab <= 3) {
      setActiveTab(activeTab + 1);
    } else if (activeTab === 4) {
      // FIX: Added this block
      navigate(`/data-pipeline/programOps/IBP/markets/ibp-dashboard`);
    }
    getProgramData();
    setOpenModal({
      ...openModal,
      isOpen: true,
      type: 'Project',
      action: 'CREATE',
    });
  };

  const handleCancel = async () => {
    if (location.pathname.includes('/ibpDashboard/gdoTabs')) {
      navigate('/data-pipeline/programOps/IBP/markets/ibpDashboard');
      return;
    }

    navigate('/data-pipeline/programOps/IBP/markets/ibp-dashboard');
  };

  const handleUpdateSave = async () => {
    if (activeTab == 0) {
      if (!isFormValid()) {
        alert('Please fill in all mandatory fields.');
        return;
      }
      const newPrgName = formData.program?.trim();
      const oldPrgName = programName?.trim();

      setProgramError('');
      setSnackbarLoading(true);

      const result = await editIbpGdo(formData.id, {
        programName: newPrgName,
        attributes: {
          description: formData.description || '',
        },
      });
      const apiError = getApiErrorMessage(result, 'Unable to update GDO.');
      if (apiError) {
        setProgramError(apiError);
        setSnackbarLoading(false);
        return;
      }

      const nextTab = activeTab + 1;
      setActiveTab(nextTab);
      setOpenSnackBar(true);
      setSnackbarLoading(false);

      if (location.pathname.includes('/ibpDashboard/gdoTabs')) {
        navigate(
            `${location.pathname}?tab=${nextTab}&programID=${formData.id}&programName=${encodeURIComponent(
            newPrgName,
          )}&isEdited=true`,
        );
      } else {
        navigate(
            `/data-pipeline/ibp-dashboard/programs-tab?programID=${formData.id}&programName=${encodeURIComponent(
            newPrgName,
          )}&isEdited=true`,
        );
      }

      setOpenModal({
        ...openModal,
        isOpen: true,
        type: 'Project',
        action: 'EDIT',
      });
    } else if (activeTab >= 1 && activeTab <= 3) {
      setActiveTab(activeTab + 1); // Go to Jobs/Pipelines (index 3)
    } else if (activeTab === 4) {
      // FIX: Added this block
      navigate(`/data-pipeline/programOps/IBP/markets/ibp-dashboard`);
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    if (alertId) {
      const configData = formData;
      const emailConfig = channels['Email'];
      if (emailConfig) {
        configData.emailConfig = emailConfig;
      } else if (configData.emailConfig) {
        configData.emailConfig = {};
      }
      const serviceNowConfig = channels['Service Now'];
      if (serviceNowConfig) {
        configData.serviceNowConfig = serviceNowConfig;
      } else if (configData.serviceNowConfig) {
        configData.serviceNowConfig = {};
      }
      const teamsConfig = channels['Teams'];
      if (teamsConfig) {
        configData.teamsConfig = teamsConfig;
      } else if (configData.teamsConfig) {
        configData.teamsConfig = {};
      }
      if (!configId) {
        configData.isCreate = true;
      }
      configData.variables = variables;
      configData.maintenanceWindows = maintenanceWindows;
      configData.groupingAlertsConfig = groupingAlertsConfig;
      const result = await updateAlertConfig(alertId, configData);
      setLoading(false);
      return result;
    } else {
      const requestBody = {
        program: formData.program,
        project: formData.project,
        sector: formData.sector,
        ruleName: formData.ruleName,
        alertType: formData.alertType,
        sourceConfig: formData.sourceConfig,
      };
      const response = await saveAlertConfig(requestBody);
      setLoading(false);
      if (response.data?._id) {
        setAlertId(response.data._id);
      }
      return response;
    }
  };

  const isFormValid = () => {
    const mandatoryFieldsValid = formData.program && formData.program.trim();
    return mandatoryFieldsValid;
  };

  const fetchData = async () => {
    if (breadcrumbPrefixLabel === 'All GDOs') {
      return;
    }
    setLoading(true);
    setHasAccess(true);
    const result: any = await getAlertKpiProgramsSectors(true);
    if(result?.status === 403) {
      setHasAccess(false);
      setLoading(false);
      return;
    }
    if (result.data) {
      setProgramOptions(result.data.programs);
      setSectorOptions(result.data.sectors);
    }
    setLoading(false);
  };

  const fetchConfig = async () => {
    setLoading(true);
    const response: any = await getConfigDetails(configId, {});
    if (response.data?.alertConfig) {
      const alertConfig = response.data.alertConfig;
      setFormData({
        program: alertConfig.program,
        project: alertConfig.project,
        sector: alertConfig.sector,
        ruleName: alertConfig.ruleName,
        alertType: alertConfig.alertType,
        sourceConfig: alertConfig.sourceConfig,
        emailConfig: alertConfig.emailConfig,
        serviceNowConfig: alertConfig.serviceNowConfig,
        teamsConfig: alertConfig.teamsConfig,
        isCreate: false,
      });
      setMaintenanceWindows(alertConfig.maintenanceWindows);
      setVariables(alertConfig.variables);
      setGroupingAlertsConfig(alertConfig.groupingAlertsConfig);
      const _channels = {} as any;
      if (alertConfig.emailConfig?.emailSubject) {
        _channels['Email'] = alertConfig.emailConfig;
      }
      if (alertConfig.serviceNowConfig?.payload) {
        alertConfig.serviceNowConfig.payload = JSON.stringify(alertConfig.serviceNowConfig.payload);
        _channels['Service Now'] = alertConfig.serviceNowConfig;
      }
      if (alertConfig.teamsConfig?.teamsMail) {
        _channels['Teams'] = alertConfig.teamsConfig;
      }
      setChannels(_channels);
    }
  };

  useEffect(() => {
    fetchData();
    if (configId) {
      fetchConfig();
    }
  }, []);

  const trimmedProgram = formData?.program?.trim() || '';
  const isProgramInvalid = !trimmedProgram || trimmedProgram.length > 64;

  // ===================================================================
  // START: STATE PRESERVATION FIX
  // Move Alert form state and initial data definitions here
  // ===================================================================
  const defaultBodyText = `SLA Status of {{Project_Name}}: {{SLA_Status}} for Program: ${
    programName || ''
  }`;

  // ===================================================================
  // START: TYPESCRIPT FIX
  // Apply the AlertData type to initialAlertData
  // ===================================================================
  const initialAlertData: AlertData = {
    // ===================================================================
    // END: TYPESCRIPT FIX
    // ===================================================================
    hopName: '',
    alertType: '',
    channel: 'Email',
    templateName: '',
    emailBody: '', // Will be overridden by defaultBodyText on add
    recipients: '',
    deliveryChannel: '',
    projects: [] as string[],

    // Email config
    emailSubject: '',

    // Service Now config
    payload: {
      description: '',
      ConfigurationItem: '',
      resource: '',
      metric_name: '',
      AssignmentGroup: '',
      severity: '2',
      source: 'PepWise',
      type: 'incident',
    },
    serviceNowUrl: '',

    // Teams config
    title: '',
    body: '',
    teamsMail: '',
  };

  const [newAlertData, setNewAlertData] = useState<AlertData>(initialAlertData);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAlertId, setEditingAlertId] = useState<string | number | null>(null);

  const headerGdoName = decodeURIComponent(queryParams.get('programName') || programDetail?.Program_Name || programDetail?.programName || '');
  const pageHeaderTitle = [
    ...(breadcrumbPrefixLabel === 'All GDOs'
      ? [{
          text: 'All GDOs',
          link: '/data-pipeline/programOps/IBP/markets/ibpDashboard',
        },]
        : [
        {
          text: breadcrumbPrefixLabel,
          link: breadcrumbLink,
        },
      ]),
      {
        text: isEdited ? 'Edit GDO' : 'Create GDO',
      },
      ...(activeTab > 0 && headerGdoName
        ? [
          {
            text: `GDO Name: ${headerGdoName}`,
          },
        ]
        : []),
      ];

  if (loading) {
    return <Loader />;
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <PageWrapper>
      <PageHeader
        title={pageHeaderTitle}
        icon={DashboardIcon}
      >
        {showUploadAction && onUploadClick && (
          <Button
            variant="text"
            onClick={() => setBulkUploadModalOpen(true)}
            sx={{
              height: '43px',
              mr: 2,
              color: colors.textLight.secondary,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            startIcon={
              <img
                src={UploadIcon}
                alt="Upload"
                style={{ width: '18px', height: '18px' }}
              />
            }
          >
            BULK UPLOAD GDOs
          </Button>
        )}
        <Typography
          variant="labelText"
          style={{
            color: `${colors.textLight.secondary}`,
            cursor: 'pointer',
            marginTop: '6px',
            marginRight: '25px',
          }}
          onClick={handleCancel}
        >
          CANCEL
        </Typography>
        <Button
          variant="pepwiseSecondary"
          onClick={isEdited ? handleUpdateSave : handleSaveNext}
          disabled={
            (!isEdited && !isFormValid()) ||
            (activeTab === 1 && projectData.length === 0) ||
            isProgramInvalid
          }
        >
          {isEdited ? 'UPDATE & SAVE' : activeTab === 4 ? 'SAVE & CONFIRM' : 'SAVE & NEXT'}
        </Button>
        {snackBar && <Loader />}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box
            sx={{
              backgroundColor: '#f0fff4',
              border: '1px solid #38a169',
              boxShadow: '0px 0px 12px rgba(56, 161, 105, 0.2)',
              padding: '16px 24px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '420px',
            }}
          >
            <CheckCircleIcon sx={{ color: '#38a169' }} />

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#2f855a', fontWeight: 'bold' }}>
                {isEdited ? 'GDO edited Successfully' : 'GDO Saved Successfully'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4a5568' }}>
                GDO has been successfully {isEdited ? 'edited' : 'saved'} in the dashboard.
              </Typography>
            </Box>

            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Snackbar>
        <SuccessConfirmationDialog
          open={openDialogSuccess}
          onClose={() => {
            setOpenDialogSuccess(false);
            handleCancel();
          }}
        />
        <ErrorDialog
          open={openErrorDialog}
          onClose={() => {
            setOpenErrorDialog(false);
            setErrorMessage('');
          }}
          errorMessage={errorMessage}
        />
      </PageHeader>
      <GBulkUploadModal
        open={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
      />
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="inherit"
        variant="fullWidth"
        TabIndicatorProps={{ style: { backgroundColor: '#009BD8', height: '3px' } }}
        sx={{
          px: 4,
          mt: '16px',
          '& .MuiTabs-flexContainer': {
            width: '100%',
            'overflow-y': 'hidden',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: 'white !important',
            height: '4px !important',
          },
          '& .MuiTab-root': {
            ...theme.typography.tabLabel,
            color: '#F5F5F5 !important',
            textTransform: 'none',
            minHeight: '50px',
            height: '50px',
            padding: '12px 0px',
            flex: '1 1 20%',
            width: '20% !important',
            maxWidth: '20% !important',
            backgroundColor: '#003771 !important',
            opacity: '1 !important',
          },
          '& .MuiTab-root.Mui-selected': {
            color: '#009BD8 !important',
            backgroundColor: '#ffffff !important',
            borderBottom: '9px solid #009BD8 !important',
          },
        }}
      >
        <Tab label="GDO" />
        <Tab label="Stages" disabled={!formData?.id || formData?.program.length > 64} />
        <Tab label="Jobs/Pipelines" disabled={!formData?.id || formData?.program.length > 64} />
        <Tab label="SLAs" disabled={!formData?.id || formData?.program.length > 64} />

        <Tab label="Alerts" />
      </Tabs>

      {/* Form Section-fU */}
      {activeTab === 0 && (
        <ProgramTab
          sourceData={formData}
          setSourceData={setFormData}
          programOptions={programOptions}
          sectorOptions={sectorOptions}
          isGdoMode={breadcrumbPrefixLabel === 'All GDOs'}
          programError={programError}
          setProgramError={setProgramError}
        />
      )}
      {activeTab === 1 && (
        <ProjectTab
          open={openModal.isOpen}
          handleEditClose={handleEditClose}
          programData={openModal}
          programID={programID}
          programName={programName}
          setProjectData={setProjectData}
          projectData={projectData}
          isGdoMode={breadcrumbPrefixLabel === 'All GDOs' || programName?.startsWith('GDO-')}
        />
      )}
      {activeTab === 2 && <JobsPipelinesTab projectData={projectData} />}
      {activeTab === 3 && (
        <SLATab
          handleEditClose={handleEditClose}
          programData={openModal}
          programID={programID}
          programName={programName}
          setProjectData={setProjectData}
          projectData={projectData}
        />
      )}
      {activeTab === 4 && (
        <IbpAlertsTab
          sourceData={formData}
          setSourceData={setFormData}
          programOptions={programOptions}
          sectorOptions={sectorOptions}
          projectData={projectData}
          programName={programName}
          programID={programID}
          showAddForm={showAddForm}
          setShowAddForm={setShowAddForm}
          newAlertData={newAlertData}
          setNewAlertData={setNewAlertData}
          editingAlertId={editingAlertId}
          setEditingAlertId={setEditingAlertId}
          initialAlertData={initialAlertData}
          defaultBodyText={defaultBodyText}
        />
      )}
    </PageWrapper>
  );
};

export default ProgramTabDashboardIbp;
